import React from 'react';
import {
  Download,
  RefreshCw,
  Loader2,
  ListChecks,
} from 'lucide-react';
import { SearchSelect } from '../../components/shared';
import { useAdminReports } from '../../hooks/useAdminReports';
import { AdminPageHeader, AdminGuideCallout } from '../../components/admin/guidance';
import { ADMIN_PAGE_GUIDES } from '../../utils/adminPageGuides';

const AdminReports = () => {
  const {
    report,
    isLoading,
    error,
    reportType,
    setReportType,
    reportTypeOptions,
    period,
    setPeriod,
    periodOptions,
    refetch
  } = useAdminReports('bookings', 'month');

  const handleDownload = () => {
    // TODO: Implement CSV/PDF download
    console.log('Download report:', report);
  };

  if (error) {
    return (
      <div className="space-y-5">
        <AdminPageHeader
          title="Reports"
          subtitle="Generate and view detailed platform reports"
        />
        <div className="rounded-2xl border border-red-200 dark:border-red-800/50 bg-white dark:bg-gray-900 p-6 text-center shadow-sm">
          <p className="text-red-600 dark:text-red-400">Failed to load report. Please try again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <AdminPageHeader
        title="Reports"
        subtitle="Generate and view detailed platform reports"
        actions={
          <button
            type="button"
            onClick={handleDownload}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 !rounded-xl hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors text-sm font-medium"
          >
            <Download className="w-4 h-4" />
            Download
          </button>
        }
      />

      <AdminGuideCallout
        title={ADMIN_PAGE_GUIDES.reports.title}
        steps={ADMIN_PAGE_GUIDES.reports.steps}
        icon={ListChecks}
      />

      <div className="rounded-2xl border border-gray-200/80 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-4 sm:p-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <SearchSelect
            label="Report Type"
            value={reportType}
            onChange={setReportType}
            options={reportTypeOptions}
          />
          <SearchSelect
            label="Period"
            value={period}
            onChange={setPeriod}
            options={periodOptions}
          />
          <div className="flex items-end">
            <button
              type="button"
              onClick={() => refetch()}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-300 dark:border-gray-600 !rounded-xl bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 text-sm font-medium"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="rounded-2xl border border-gray-200/80 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm py-20 text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-3" />
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading report…</p>
        </div>
      ) : (
        <div className="space-y-5">
          {report.summary && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {Object.entries(report.summary).slice(0, 4).map(([key, value]) => (
                <div key={key} className="rounded-2xl border border-gray-200/80 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-4 sm:p-5">
                  <p className="text-sm text-gray-600 dark:text-gray-400 capitalize">
                    {key.replace(/_/g, ' ')}
                  </p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-50 mt-1 tabular-nums">
                    {typeof value === 'number'
                      ? (key.includes('revenue') || key.includes('amount') || key.includes('total'))
                        ? `$${value.toFixed(2)}`
                        : value.toLocaleString()
                      : value}
                  </p>
                </div>
              ))}
            </div>
          )}

          <div className="rounded-2xl border border-gray-200/80 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-4 sm:p-5">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50 mb-4">Report Summary</h2>
            <div className="space-y-4">
              {report.summary && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Summary</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {Object.entries(report.summary).map(([key, value]) => (
                      <div key={key} className="flex justify-between items-center p-2.5 rounded-xl bg-gray-50 dark:bg-gray-800/60">
                        <span className="text-xs text-gray-600 dark:text-gray-400 capitalize">
                          {key.replace(/_/g, ' ')}
                        </span>
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-50 tabular-nums">
                          {typeof value === 'number'
                            ? (key.includes('revenue') || key.includes('amount') || key.includes('total'))
                              ? `$${value.toFixed(2)}`
                              : value.toLocaleString()
                            : value}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {report.by_status && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">By Status</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {Object.entries(report.by_status).map(([status, count]) => (
                      <div key={status} className="p-2.5 rounded-xl bg-gray-50 dark:bg-gray-800/60">
                        <span className="text-xs text-gray-600 dark:text-gray-400 capitalize">{status}</span>
                        <p className="text-lg font-bold text-gray-900 dark:text-gray-50 tabular-nums">{count}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {report.by_month && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">By Month</h3>
                  <div className="space-y-1.5">
                    {Object.entries(report.by_month).map(([month, value]) => (
                      <div key={month} className="flex justify-between items-center p-2.5 rounded-xl bg-gray-50 dark:bg-gray-800/60">
                        <span className="text-sm text-gray-600 dark:text-gray-400">{month}</span>
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-50 tabular-nums">
                          {typeof value === 'number'
                            ? (reportType === 'revenue' || reportType === 'bookings')
                              ? reportType === 'revenue'
                                ? `$${value.toFixed(2)}`
                                : value.toLocaleString()
                              : value.toLocaleString()
                            : value}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminReports;
